import { useEffect, useImperativeHandle, useRef, forwardRef, useId } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
        }
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

let scriptLoadPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Clear the cached promise on failure so a later mount (e.g. after the
      // user's network recovers) retries instead of reusing a permanently-
      // rejected promise for the rest of the page session.
      scriptLoadPromise = null;
      reject(new Error("Failed to load Turnstile script"));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

export interface TurnstileHandle {
  reset: () => void;
}

interface TurnstileProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
}

export const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(
  ({ onVerify, onExpire }, ref) => {
    // Strip colons the same way client/src/components/ui/chart.tsx does for
    // its chart id — useId()'s raw output (":r0:") isn't a valid CSS
    // selector/DOM id fragment as-is.
    const containerId = useId().replace(/:/g, "");
    const widgetIdRef = useRef<string | null>(null);

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }));

    useEffect(() => {
      let cancelled = false;

      loadTurnstileScript()
        .then(() => {
          if (cancelled || !window.turnstile) {
            return;
          }

          const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
          if (!siteKey) {
            console.error("VITE_TURNSTILE_SITE_KEY is not configured");
            return;
          }

          widgetIdRef.current = window.turnstile.render(`#turnstile-${containerId}`, {
            sitekey: siteKey,
            callback: onVerify,
            "expired-callback": onExpire,
          });
        })
        .catch((error) => {
          // Non-goal per spec: no user-facing message on script load failure.
          // Still log it so it's not a silent, unhandled rejection.
          console.error(error);
        });

      return () => {
        cancelled = true;
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <div id={`turnstile-${containerId}`} />;
  }
);

Turnstile.displayName = "Turnstile";
